// @ts-nocheck
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { DynamicDropdownPresentationProps } from "@store/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import {
    autocompleteFontStyles,
    autocompleteLinkWithImageStyles,
    autocompleteOptionStyles,
} from "@store/Themes/common/mixins/autocomplete";
import { imageStyles } from "@store/Themes/common/mixins/image";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import { inlineFormWithLabelProps } from "@store/Themes/common/props/inlineForm";
import { ellipsis } from "@store/Themes/common/mixins/ellipsis";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
// import { focusOutlineStyles, focusUpdateStyles } from "@toolkit/Themes/common/mixins"; Bunzl removed code
import { css } from "styled-components";
// Store Custom Code
import { focusOutlineStyles, focusStyles, focusUpdateStyles } from "@toolkit/Themes/common/mixins";

export interface AutocompleteProps {
    wrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
    note?: TypographyPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    optionWrapper?: InjectableCss;
    optionWithImageWrapper?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    imageWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    erpNumber?: TypographyPresentationProps;
}

export const autocompleteGetLinkProps = (
    color?: string,
    hoverColor?: string | null,
    underline?: boolean,
    hoverUnderline?: boolean,
): LinkPresentationProps => ({
    css: css`
        ${({ theme }) => {
            return linkStyles(
                color ?? theme.design.autocomplete.link.color,
                hoverColor ?? theme.design.autocomplete.link.hoverColor,
                underline ?? theme.design.autocomplete.link.underline,
                hoverUnderline ?? theme.design.autocomplete.link.hoverUnderline,
            );
        }}
        /* Bunzl custom code */
        ${focusStyles(true)}
        position: relative;
        z-index: 1;
        width: 100%;
        padding: ${pxToRem(5, 0)};
    `,
    typographyProps: {
        ellipsis: true,
        css: css`
            z-index: -1;
            width: 100%;
            text-align: left;
        `,
    },
});

export const autocompleteProps: AutocompleteProps = {
    headerText: {
        css: css`
            display: block;
            margin: ${pxToRem(18, 0, 5, 0)};
            padding-bottom: ${pxToRem(5)};
            text-align: left;

            /* Store Removed Code - text-transform */
            font-weight: ${x => x.theme.design.typography.weight.bold};
            border-bottom: 1px solid ${getColor("common.borderDark")}; /* Store Custom Code */

            &:first-child {
                margin-top: 0;
            }
        `,
    },
    link: autocompleteGetLinkProps(),
    focusedLink: mergeProps<AutocompleteProps["focusedLink"]>(autocompleteGetLinkProps(), {
        css: css`
            color: ${getColor("primary")};

            &:not(:focus-visible) {
                ${x => focusOutlineStyles(`-${x.theme.focus.width}`)}
            }
        `,
    }),
    note: {
        italic: true,
        css: css`
            font-size: ${x => x.theme.design.typography.size.smallest};
        `,
    },
    productPartNumbers: {
        wrapper: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                margin-top: ${pxToRem(1)};
                font-size: ${x => x.theme.design.typography.size.smallest};
                color: inherit;
                line-height: inherit;
            `,
        },
        innerWrapper: {
            css: css`
                ${inlineSeparatorStyles({
                    spacing: "1em",
                    width: pxToRem(1),
                    color: "currentColor",
                    spacingType: "padding",
                })}
                margin-bottom: 0;
                ${ellipsis()}
            `,
        },
    },
    imageWrapper: {
        css: css`
            ${x => imageStyles(x.theme.design.product.image.small.ratio)}
            width: ${pxToRem(50)};
            flex: 0 0 auto;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            width: 0%;
            flex: 1 0 auto;
            flex-direction: column;
        `,
    },
    searchDynamicDropdown: mergeProps<AutocompleteProps["searchDynamicDropdown"]>(inlineFormWithLabelProps.field, {
        cssOverrides: {
            formField: css`
                flex-grow: 999999;
                min-width: 50%;
            `,
            option: autocompleteOptionStyles(),
        },
    }),
    optionWrapper: {
        css: css`
            ${autocompleteFontStyles()}
            display: flex;
            align-items: center;
            cursor: pointer;
        `,
    },
    optionWithImageWrapper: {
        css: css`
            ${autocompleteFontStyles()}
            ${autocompleteLinkWithImageStyles()}
        `,
    },
    title: {
        ellipsis: true,
        css: css`
            margin-bottom: ${pxToRem(8)}; /* Store Custom Code */
        `,
    },
    erpNumber: {
        css: css`
            float: left; /* Prevent underline effect */
        `,
    },
};
